import { createContext, useContext, useEffect, useState } from "react";
import { getAllOrders, getOnGoingOrders, getWaitingOrders, IOrderSearchFilters } from "../services/orderService";
import { useOrderSocket } from '../hooks/useOrderSocket';
import { IOrder } from "../interfaces/IOrder";
import { PUBLIC_ROUTES } from "../constants";
import { checkPublicRoute } from "../utils";
import { AuthContext } from "./AuthContext";

interface OrdersContextType {
  orders: IOrder[];
  totalOrders: number;
  loadAvailableOrders: (page: number, pageSize: number, filters?: IOrderSearchFilters, startDate?: string | null, endDate?: string | null) => Promise<IOrder[]>;
  addOrder: (client: IOrder) => void;
  editOrder: (client: IOrder) => void;
  onGoingOrders: IOrder[];
  loadOnGoingOrders: (forceLoad: boolean) => Promise<void>;
  loadWaitingOrders: () => Promise<void>;
  waitingOrders: IOrder[]
}

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

export const OrdersProvider: React.FC = ({ children }) => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [onGoingOrders, setOnGoingOrders] = useState<IOrder[]>([]);
  const [waitingOrders, setWaitingOrders] = useState<IOrder[]>([]);
  const token = localStorage.getItem("token");
  const { adminData } = useContext(AuthContext);

  const loadAvailableOrders = async (page: number, pageSize: number, filters?: IOrderSearchFilters, startDate?: string | null, endDate?: string | null) => {
    const { data: { orders, total } } = await getAllOrders(page, pageSize, filters, startDate, endDate);
    setOrders(orders);
    setTotalOrders(total);
    return orders;
  };

  const loadOnGoingOrders = async (forceLoad = false) => {
    if (onGoingOrders.length === 0 || forceLoad) {
      const response = await getOnGoingOrders();
      const { data: orders } = response;

      setOnGoingOrders(orders);
    }
  }

  const loadWaitingOrders = async () => {
    const { data: { orders } } = await getWaitingOrders();
    setOrders(orders);
    setWaitingOrders(orders);
  }

  const addOrder = (order: IOrder) => {
    setOrders((prevOrders) => [...prevOrders, order]);
    setOnGoingOrders((prevOrders) => [...prevOrders, order]);
    loadOnGoingOrders(true);
  };
  
  const editOrder = (updatedOrder: IOrder) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order
      )
    );

    setOnGoingOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order
      )
    );
  };

  // Inicializa o WebSocket apenas uma vez, independente de rotas
  useOrderSocket((data: any, eventType: string) => {
    const currentPath = window.location.pathname;
    const isPublicRoute = checkPublicRoute(currentPath, PUBLIC_ROUTES);
    console.log(' isPublicRoute:', isPublicRoute);

    // Só processa eventos se não estiver em rota pública
    if (isPublicRoute) return;

    if (eventType === 'whatsappOrder' && window.location.href.includes('backoffice')) {
        window.dispatchEvent(new CustomEvent('new-order', {
          detail: {
            message: '💐 Novo Pedido via link recebido!',
            orderCode: `#${data.code}`,
            clientName: `${data.client.first_name} ${data.client.last_name}`,
          }
      }));
    }

    if (eventType === 'storeFrontOrder'
        && !window.location.href.includes('completarPedido')
      && data.status !== 'PENDING_PAYMENT') {
        window.dispatchEvent(new CustomEvent('new-order', {
          detail: {
            message: '💐 Novo Pedido via loja online recebido!',
            orderCode: `#${data.code}`,
            clientName: `${data.client.first_name} ${data.client.last_name}`,
          }
      }));
    }

    if (eventType === 'orderDelivered' && window.location.href.includes('backoffice')) {
        window.dispatchEvent(new CustomEvent('order-delivered', {
          detail: {
            message: '✅ Pedido entregue pelo motoboy! 🛵',
            orderCode: `#${data.order.code}`,
            deliveryMan: `${data.deliveryMan.name}`,
            clientName: `${data.order.client.first_name} ${data.order.client.last_name}`,
          }
      }));
    }

    if (eventType === 'orderPaymentConfirmed' && window.location.href.includes('backoffice')) {
      console.log('Emitting payment confirmed notification with data:', data);
        window.dispatchEvent(new CustomEvent('order-payment-confirmed', {
          detail: {
            message: '💐🛒 Novo Pedido via loja online recebido!',
            orderCode: `#${data.order.code}`,
            clientName: `${data.order.client.first_name} ${data.order.client.last_name}`,
            amount: data.payment_info.amount,
            paymentType: data.payment_info.payment_type,
          }
      }));
    }

    if (eventType === 'counterOrder' && window.location.href.includes('backoffice')) {
        window.dispatchEvent(new CustomEvent('counter-order-created', {
          detail: {
            message: '🧾 Novo Pedido de balcão criado!',
            orderCode: `#${data.code}`,
            clientName: `${data.client?.first_name ?? ''} ${data.client?.last_name ?? ''}`.trim(),
          }
      }));
    }

    loadOnGoingOrders(true);
  }, adminData.store_id || undefined);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isPublicRoute = checkPublicRoute(currentPath, PUBLIC_ROUTES);
    
    if (token && !isPublicRoute) {
      if (!window.location.pathname.includes('ordensDeServico')) {
        loadOnGoingOrders();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OrdersContext.Provider value={{
        orders,
        totalOrders,
        addOrder,
        editOrder,
        loadAvailableOrders,
        onGoingOrders,
        loadOnGoingOrders,
        loadWaitingOrders,
        waitingOrders
      }}>
      {children}
    </OrdersContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrdersContext);
  if (!context) throw new Error("useOrders must be used within a ClientsProvider");
  return context;
};
